import { IonCol, IonGrid, IonRow, IonText, useIonToast } from "@ionic/react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";

import TertiaryButton from "@components/buttons/TertiaryButton";
import styles from "@components/ConsultConflict.module.css";
import DateAndTimeRange from "@components/dates/DateAndTimeRange";
import TextWithIcon from "@components/text/TextWithIcon";
import useMutationWithValidation from "@hooks/useMutationWithValidation";
import useMyExpert from "@hooks/useMyExpert";
import {
  ConsultCancelDocument,
  ConsultCancelMutation,
  ConsultCancelMutationVariables,
  ExpertGlobalRoleEnum,
  GetOutOfOfficePeriodQuery,
  RescheduleConsultEmailSendDocument,
  RescheduleConsultEmailSendMutation,
  RescheduleConsultEmailSendMutationVariables
} from "@typing/Generated";
import { IconList } from "@utils/iconUtils";
import { journeyRoute } from "@utils/routeUtils";

type Consult = NonNullable<NonNullable<GetOutOfOfficePeriodQuery["outOfOfficePeriod"]["consultConflicts"]>[number]>;

type Props = {
  consult: Consult;
};

const ConsultConflict = ({ consult }: Props) => {
  const myExpert = useMyExpert();
  const [canceled, setCanceled] = useState(false);
  const [emailResend, setEmailResend] = useState(false);

  const [cancelConsult] = useMutationWithValidation<ConsultCancelMutation, ConsultCancelMutationVariables>(
    ConsultCancelDocument,
    "consult"
  );
  const [rescheduleConsultEmailSend] = useMutationWithValidation<
    RescheduleConsultEmailSendMutation,
    RescheduleConsultEmailSendMutationVariables
  >(RescheduleConsultEmailSendDocument, "consult");

  const intl = useIntl();

  const relationship = useMemo(() => {
    const ownerRole = consult.journey.clientRoles.find(role => role.client.id === consult.journey.owner.id);
    return ownerRole?.relationship?.other ?? ownerRole?.relationship?.relationshipOption;
  }, [consult.journey.clientRoles, consult.journey.owner.id]);

  const handleConsultCancel = useCallback(() => {
    setCanceled(true);
  }, []);

  const handleRescheduleConsultEmailSend = useCallback(() => {
    setEmailResend(true);
  }, []);

  const [rescheduleEmailSentPresent, rescheduleEmailSentDismiss] = useIonToast();

  useEffect(() => {
    if (canceled) {
      cancelConsult({
        id: consult.id
      });
    }
  }, [cancelConsult, canceled, consult.id]);

  useEffect(() => {
    if (emailResend) {
      rescheduleConsultEmailSend({
        id: consult.id
      }).then(() => {
        rescheduleEmailSentPresent({
          buttons: [{ handler: () => rescheduleEmailSentDismiss(), icon: IconList.xmark }],
          cssClass: styles.toast,
          duration: 5000,
          message: intl.formatMessage({ id: "pages.expert.outOfOffice.rescheduleEmailSent" }),
          position: "top"
        });
      });
    }
  }, [
    consult.id,
    emailResend,
    intl,
    rescheduleConsultEmailSend,
    rescheduleEmailSentDismiss,
    rescheduleEmailSentPresent
  ]);

  const controlsVisible = useMemo(
    () =>
      myExpert?.expertGlobalRole === ExpertGlobalRoleEnum.MANAGER ||
      consult.journey.expertRoles?.find(role => role.expert.id === myExpert?.id),
    [consult.journey.expertRoles, myExpert?.expertGlobalRole, myExpert?.id]
  );

  return (
    <div className={styles.conflict}>
      <DateAndTimeRange endTime={consult.endTime} startTime={consult.startTime} />
      <div className={styles.info}>
        <FormattedMessage
          id="pages.expert.outOfOffice.client"
          values={{
            clientName: consult.journey.owner.fullName,
            employerName: consult.journey.owner.employer?.name
          }}
        />
      </div>
      <div>
        <FormattedMessage
          id="pages.expert.outOfOffice.lovedOne"
          values={{
            name: consult.careProfileName,
            relationship
          }}
        />
      </div>
      {controlsVisible && (
        <IonGrid>
          <IonRow>
            <IonCol>
              <TertiaryButton
                icon="edit"
                routerLink={journeyRoute({
                  journeyId: consult.journey.id,
                  linkAfterJourney: `/consults/${consult.id}/edit`,
                  myExpert
                })}
                textKey="pages.expert.outOfOffice.rescheduleConsult"
              />
            </IonCol>
            <IonCol>
              {emailResend ? (
                <IonText className={styles.disabled}>
                  <TextWithIcon
                    icon="envelope"
                    text={intl.formatMessage({ id: "pages.expert.outOfOffice.sendRescheduleMessage" })}
                  />
                </IonText>
              ) : (
                <TertiaryButton
                  icon="envelope"
                  onClick={handleRescheduleConsultEmailSend}
                  textKey="pages.expert.outOfOffice.sendRescheduleMessage"
                />
              )}
            </IonCol>
            <IonCol>
              {canceled ? (
                <IonText className={styles.disabled}>
                  <TextWithIcon
                    icon="calendarXmark"
                    text={intl.formatMessage({ id: "pages.expert.outOfOffice.cancelConsult" })}
                  />
                </IonText>
              ) : (
                <TertiaryButton
                  icon="calendarXmark"
                  onClick={handleConsultCancel}
                  textKey="pages.expert.outOfOffice.cancelConsult"
                />
              )}
            </IonCol>
          </IonRow>
        </IonGrid>
      )}
    </div>
  );
};

export default ConsultConflict;
